import type { PlayerStateSync, VideoStateSync } from '@core/player/types'
import type { NamedSet } from '@core/zustand'

import { closePlayerStates } from './helpers'
import type { PlayerGlobalState } from './types'

/**
 * Actions for shared player state
 */
export function createPlayerActions(set: NamedSet<PlayerGlobalState>) {
  return {
    /**
     * Each player on the stage reports its state to the global store.
     * Other players can subscribe and act based on other players' state.
     * @param playerState
     */
    setPlayerState: (playerState: PlayerStateSync) => {
      set(
        ({ playerStates }) => {
          const widgetId = playerState.widget_id

          return {
            playerStates: {
              ...closePlayerStates(playerStates, playerState),
              [widgetId]: playerState,
            },
          }
        },
        false,
        'playerStateSet',
      )
    },
    unsetPlayerState: (widgetId: string) => {
      set(
        (state) => {
          const { [widgetId]: _, ...playerStates } = state.playerStates || {}

          return {
            playerStates,
          }
        },
        false,
        'playerStateUnset',
      )
    },

    /**
     * Set the playback state of a video in our widgets/players. Used to resume last played time.
     * Keeping the number of videos in the store at max 30 to optimize performance.
     * @param videoState
     */
    setVideoState: (videoState: Omit<VideoStateSync, 'lastUpdatedTime'>) => {
      set((state) => {
        const videoStateWithLastUpdatedTime = {
          ...videoState,
          lastUpdatedTime: Date.now(),
        } satisfies VideoStateSync

        const currentVideoStates = state.videoStates
        const updatedVideoStates = {
          ...currentVideoStates,
          [videoStateWithLastUpdatedTime.videoId]:
            videoStateWithLastUpdatedTime,
        }

        // Performance-optimized filtering for 30+ video range
        const videoCount = Object.keys(updatedVideoStates).length
        if (videoCount <= 30) {
          return { videoStates: updatedVideoStates }
        }

        // Efficient filtering: find oldest videos to remove
        let oldestTime = Infinity
        let oldestVideoId = ''

        for (const [id, video] of Object.entries(updatedVideoStates)) {
          if (video.lastUpdatedTime < oldestTime) {
            oldestTime = video.lastUpdatedTime
            oldestVideoId = id
          }
        }

        // Remove the oldest video to keep count at 30
        const { [oldestVideoId]: _, ...filteredVideoStates } =
          updatedVideoStates

        return { videoStates: filteredVideoStates }
      })
    },

    /** Livestream relate global actions */
    setLivestreamJoinEventTracked: (videoId: string) => {
      set(
        (state) => {
          return {
            isLivestreamJoinEventTrackedByVideoId: {
              ...state.isLivestreamJoinEventTrackedByVideoId,
              [videoId]: true,
            },
          }
        },
        false,
        'setLivestreamJoinEventTracked',
      )
    },
    unsetLivestreamJoinEventTracked: (videoId: string) => {
      set(
        (state) => {
          return {
            isLivestreamJoinEventTrackedByVideoId: {
              ...state.isLivestreamJoinEventTrackedByVideoId,
              [videoId]: false,
            },
          }
        },
        false,
        'unsetLivestreamJoinEventTracked',
      )
    },

    /**
     * Whether user interacted with the mute button in any of our widgets/players
     * and what was the desired action. Possible values are `undefined | true | false`
     * where `undefined` means that user didn't interact yet.
     * @param isUserMuted
     */
    setUserMuted: (isUserMuted: boolean) => {
      set({ isUserMuted }, false, 'setUserMuted')
    },
  }
}
